/**
 * Browser detection utilities for handling in-app browser issues
 * 
 * In-app browsers (Instagram, Facebook) often have OAuth redirect issues.
 * This module helps detect these browsers and provides solutions.
 */

/**
 * Check if the current browser is an in-app browser (Instagram or Facebook)
 */
export const isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  return /Instagram|FBAN|FBAV/i.test(ua);
};

/**
 * Get the type of in-app browser
 * @returns {'instagram' | 'facebook' | null}
 */
export const getInAppBrowserType = () => {
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV/i.test(ua)) return 'facebook';
  return null;
};

/**
 * Check if the device is running Android
 */
export const isAndroid = () => /Android/i.test(navigator.userAgent);

/**
 * Check if the device is running iOS
 */
export const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * Check if the device is mobile
 */
export const isMobile = () => /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * Try to open the URL in an external browser (Chrome on Android)
 * Uses Android intent:// URL scheme to force Chrome to open
 * 
 * @param {string} url - The URL to open
 * @returns {boolean} - True if intent URL was used (Android), false otherwise (iOS/other)
 */
export const openInExternalBrowser = (url) => {
  if (isAndroid()) {
    // Remove protocol from URL for intent format
    const cleanUrl = url.replace(/^https?:\/\//, '');
    
    // Build intent URL for Chrome
    // Format: intent://HOST#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=ENCODED_URL;end
    const intentUrl = `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
    
    window.location.href = intentUrl;
    return true;
  }
  
  // iOS and other platforms - can't programmatically open in Safari
  return false;
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - True if successful
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

export default {
  isInAppBrowser,
  getInAppBrowserType,
  isAndroid,
  isIOS,
  isMobile,
  openInExternalBrowser,
  copyToClipboard,
};






